import { type CiaLaneView } from '@m/types/api';

export const CIA_NEARBY_GATE_LAST_CHECKED_KEY = 'cia_nearby_gate_last_checked_at';
export const CIA_NEARBY_GATE_COOLDOWN_MS = 3 * 60 * 1000; // 3 minutes

export type CiaSiteLaneGroup = {
  lanes: CiaLaneView[];
  siteId: number;
  siteName: string;
};

export type CiaNearbyPromptVariant = 'single' | 'multiLane' | 'multiZone';

export function groupCiaLanesBySite(lanes: CiaLaneView[]): CiaSiteLaneGroup[] {
  const groups = new Map<number, CiaSiteLaneGroup>();

  for (const lane of lanes) {
    const existing = groups.get(lane.siteId);
    if (existing) {
      existing.lanes.push(lane);
      continue;
    }
    groups.set(lane.siteId, {
      siteId: lane.siteId,
      siteName: lane.siteName,
      lanes: [lane],
    });
  }

  return Array.from(groups.values());
}

export function getCiaNearbyPromptVariant(
  grouped: CiaSiteLaneGroup[],
): CiaNearbyPromptVariant | null {
  if (grouped.length === 0) return null;
  if (grouped.length > 1) return 'multiZone';
  if (grouped[0].lanes.length === 1) return 'single';
  return 'multiLane';
}

export function getCiaNearbyLastCheckedAt(): number | null {
  try {
    const raw = sessionStorage.getItem(CIA_NEARBY_GATE_LAST_CHECKED_KEY);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function stampCiaNearbyLastCheckedAt(now = Date.now()): void {
  try {
    sessionStorage.setItem(CIA_NEARBY_GATE_LAST_CHECKED_KEY, String(now));
  } catch {
    // Ignore quota / private-mode failures; cooldown becomes best-effort.
  }
}

export function isCiaNearbyCooldownActive(
  now = Date.now(),
  cooldownMs = CIA_NEARBY_GATE_COOLDOWN_MS,
): boolean {
  const lastCheckedAt = getCiaNearbyLastCheckedAt();
  if (lastCheckedAt == null) return false;
  return now - lastCheckedAt < cooldownMs;
}
